// Pulls structure out of the WordPress HTML that `npm run fetch:posts` bakes
// into wpPosts.json, so BlogDetail can render parts of a post differently
// rather than dumping the whole blob into one dangerouslySetInnerHTML.
//
// Everything here runs in a browser -- CRA in the client, headless Chrome
// during the react-snap crawl -- so DOMParser is available in both. It is
// still guarded: if it ever runs somewhere without one, the post falls back
// to rendering exactly as it does today rather than throwing.

const FAQ_HEADING = /^\s*(faqs?|frequently\s+asked\s+questions)\b/i;

function parseDocument(html) {
  if (typeof DOMParser === "undefined" || !html) return null;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  return { doc, blocks: Array.from(doc.body.children) };
}

function parseBlocks(html) {
  const parsed = parseDocument(html);
  return parsed ? parsed.blocks : null;
}

function headingLevel(el) {
  return /^H[1-6]$/.test(el.tagName) ? Number(el.tagName[1]) : 0;
}

// Authors write the Q&A two different ways depending on which post you look
// at: most use a heading per question, a couple use a paragraph that is
// nothing but a <strong>. Both mean "this is a question".
function isQuestion(el, faqLevel) {
  const level = headingLevel(el);
  // Normally a question sits below the FAQ heading. The trailing "?" catches
  // the ones written at the same level by mistake, which would otherwise be
  // inside the section but not recognised as starting an answer.
  if (level) return level > faqLevel || el.textContent.trim().endsWith("?");
  if (el.tagName !== "P" || el.children.length !== 1) return false;
  const only = el.children[0];
  return (
    only.tagName === "STRONG" &&
    el.textContent.trim() === only.textContent.trim() &&
    el.textContent.trim().length > 0
  );
}

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

// Advice paragraphs open with a bold lead-in -- "How to fix it:", "Tip:" --
// and then read as ordinary body copy, so the actionable half of a post looks
// identical to the explanation around it. Tagging them lets the stylesheet
// lift them out. CSS cannot select on text, so it has to happen here.
const CALLOUT_LEAD =
  /^(how to fix it|what to do|what you should do|who it works for|what to expect|best for|tip|note|solution|remember|important|key takeaway)\b/i;

function markCallouts(blocks) {
  let marked = 0;
  for (const el of blocks) {
    if (el.tagName !== "P") continue;
    const lead = el.firstElementChild;
    if (!lead || lead.tagName !== "STRONG") continue;
    if (!CALLOUT_LEAD.test(lead.textContent.trim())) continue;
    el.classList.add("wp-callout");
    marked += 1;
  }
  return marked;
}

// A cell holding a figure rather than prose: it has a digit, and few enough
// letters that "Maximum 78,000" or "3 kW" still counts while a sentence does
// not. Used to decide which columns to align right.
function isFigureCell(text) {
  return /\d/.test(text) && text.replace(/[^a-zA-Z]/g, "").length <= 12;
}

/**
 * Splits the number off a sub-heading the author numbered themselves.
 *
 * 23 of the 78 h3s across the posts are steps -- "1. Register on the Portal".
 * They get a step numeral instead of the dot marker every other sub-heading
 * carries, because a dot beside a digit is the same redundancy the section
 * badges were built to remove.
 */
function markSubheadings(blocks, doc) {
  for (const el of blocks) {
    if (el.tagName !== "H3") continue;
    const raw = el.textContent.replace(/\s+/g, " ").trim();
    const numbered = /^(\d+)[.)]\s+(.*)$/.exec(raw);
    if (!numbered) continue;

    const badge = doc.createElement("span");
    badge.className = "sub-num";
    badge.setAttribute("aria-hidden", "true");
    badge.textContent = numbered[1];
    el.textContent = numbered[2];
    el.insertBefore(badge, el.firstChild);
    el.classList.add("has-sub-num");
  }
}

/**
 * Turns WordPress's tables into tables the stylesheet can actually style.
 *
 * The block editor writes a header row as <td><strong>Heading</strong></td>,
 * never <th> -- so `.wp-content th`, tint and all, has never once applied to
 * any table on the site, and every cell has carried identical weight and
 * borders. Promoting that row is what makes the existing rule work, and it
 * gives screen readers real column headers to announce cells against.
 */
function enhanceTables(doc) {
  for (const table of Array.from(doc.body.querySelectorAll("table"))) {
    const rows = Array.from(table.rows);
    if (!rows.length) continue;

    // Only a first row that is ENTIRELY bold cells is a header. A table whose
    // first row is ordinary data keeps it as data.
    const first = Array.from(rows[0].cells);
    const isHeaderRow =
      !table.tHead &&
      first.length > 0 &&
      first.every((cell) => {
        const strong = cell.querySelector("strong, b");
        return strong && cell.textContent.trim() === strong.textContent.trim();
      });

    if (isHeaderRow) {
      const thead = doc.createElement("thead");
      const tr = doc.createElement("tr");
      for (const cell of first) {
        const th = doc.createElement("th");
        th.setAttribute("scope", "col");
        // Unwrapped: the cell is a heading now, so the bold is redundant and
        // would fight the header's own weight.
        th.textContent = cell.textContent.replace(/\s+/g, " ").trim();
        tr.appendChild(th);
      }
      thead.appendChild(tr);
      rows[0].parentNode.removeChild(rows[0]);
      table.insertBefore(thead, table.firstChild);
    }

    // Right-align columns that carry figures, so amounts stack digit over
    // digit. Never the first column -- that is the row's label, whatever it
    // contains ("1 kW" reads as a name, not a quantity).
    const bodyRows = Array.from(table.tBodies[0] ? table.tBodies[0].rows : []);
    const columns = Math.max(...bodyRows.map((r) => r.cells.length), 0);
    for (let col = 1; col < columns; col += 1) {
      const cells = bodyRows
        .map((r) => r.cells[col])
        .filter(Boolean);
      if (!cells.length) continue;
      const figures = cells.filter((c) =>
        isFigureCell(c.textContent.trim())
      ).length;
      if (figures / cells.length < 0.6) continue;
      for (const cell of cells) cell.classList.add("is-figure");
      const head = table.tHead && table.tHead.rows[0];
      if (head && head.cells[col]) head.cells[col].classList.add("is-figure");
    }

    // A rounded, scrollable shell so a narrow screen scrolls the table rather
    // than squeezing a number onto three lines.
    if (!table.parentElement.classList.contains("wp-table")) {
      const shell = doc.createElement("div");
      shell.className = "wp-table";
      table.parentNode.insertBefore(shell, table);
      shell.appendChild(table);
    }
  }
}

/**
 * Gives every top-level h2 a stable id and collects them for the contents
 * list, and demotes any h1 in the body to h2.
 *
 * The h1 demotion is not hypothetical: can-solar-panels-work-during-monsoon-
 * season was written with eight Heading 1 blocks, which on top of the page's
 * own title made nine h1s on one document. Fixing it in wp-admin fixes that
 * post; doing it here fixes every post anyone writes that way next.
 */
function indexHeadings(blocks, doc) {
  const headings = [];
  const used = new Set();

  blocks.forEach((el, i) => {
    if (el.tagName === "H1") {
      const h2 = doc.createElement("h2");
      h2.innerHTML = el.innerHTML;
      // Class carries the WordPress block styling; keep it so the demoted
      // heading still looks like the h2s around it.
      if (el.className) h2.className = el.className;
      el.replaceWith(h2);
      blocks[i] = h2;
    }

    const el2 = blocks[i];
    if (el2.tagName !== "H2") return;

    const raw = el2.textContent.replace(/\s+/g, " ").trim();
    if (!raw) return;

    // Sections are not a numbered list, and numbering them made them read as
    // one. "How Does Commercial & Industrial Solar Reduce Electricity Bills?"
    // is not item one of anything; a numeral in front of it turns a heading
    // into a pointer. Flagged in SEO review, and right -- 89 of the 108
    // numerals on the site were added here rather than written by anyone.
    //
    // A number the AUTHOR wrote is different: it is content. The 10-causes
    // post promises ten and counts them off, so those stay exactly as typed,
    // inline, the way WordPress emitted them. The number is still split off
    // for the id alone, which keeps anchors free of leading digits.
    const explicit = /^(\d+)[.)]\s+(.*)$/.exec(raw);
    const idBase = explicit ? explicit[2] : raw;
    const text = raw;

    let id = slugify(idBase) || `section-${i}`;
    let n = 2;
    while (used.has(id)) id = `${slugify(idBase)}-${n++}`;
    used.add(id);

    el2.setAttribute("id", id);
    headings.push({ id, text });
  });

  return headings;
}

/**
 * Lifts the opening paragraph out of the body so it can be shown as the
 * summary panel.
 *
 * Only used where WordPress auto-generated the excerpt from that same
 * paragraph, which is nine of the thirteen posts. Promoting it is what makes
 * the panel possible everywhere without either repeating the paragraph three
 * inches further down or inventing a summary that the author never wrote.
 * The other four open at an <h2> and have a real hand-written excerpt, so
 * there is nothing to lift and nothing changes for them.
 */
function takeLede(blocks) {
  if (!blocks.length || blocks[0].tagName !== "P") return null;
  const lede = blocks.shift();
  return lede.innerHTML.trim() ? lede.innerHTML : null;
}

/**
 * Splits a post into its article body, a structured FAQ list, the headings
 * that make up its table of contents, and the summary panel's contents.
 *
 * `excerptHtml` is the post's WordPress excerpt. It is passed in so the
 * decision about where the summary comes from lives next to the parsing that
 * has to act on it.
 */
export function prepareBlogContent(html, excerptHtml) {
  const parsed = parseDocument(html);
  if (!parsed)
    return { articleHtml: html, faqs: [], headings: [], summaryHtml: null };
  const { doc, blocks } = parsed;

  const start = blocks.findIndex(
    (el) => headingLevel(el) >= 2 && FAQ_HEADING.test(el.textContent)
  );

  // An author-written excerpt is used as-is and the body is left alone. An
  // auto-generated one is discarded in favour of the paragraph it was copied
  // from, which is then removed from the article.
  const authored = !excerptRepeatsBody(excerptHtml, html);
  const summaryHtml = authored ? excerptHtml : takeLede(blocks);

  // No FAQ section: still worth a pass for the heading ids and h1 demotion,
  // so the four posts without one get a contents list like the rest.
  if (start === -1) {
    enhanceTables(doc);
    markSubheadings(blocks, doc);
    markCallouts(blocks);
    const headings = indexHeadings(blocks, doc);
    return {
      articleHtml: blocks.map((el) => el.outerHTML).join(""),
      faqs: [],
      headings,
      summaryHtml,
    };
  }

  // takeLede may have removed blocks[0], so the index found above is stale.
  const faqStart = blocks.findIndex(
    (el) => headingLevel(el) >= 2 && FAQ_HEADING.test(el.textContent)
  );
  const faqLevel = headingLevel(blocks[faqStart]);

  // The section runs until the next heading of the same or higher rank --
  // unless that heading is itself a question, in which case the author simply
  // wrote a Q&A item at the wrong level and it belongs in the accordion.
  //
  // ground-mounted-vs-rooftop-solar-roi does exactly that: its first question
  // is an <h3> and the next two are <h2>s. Treating those as the end of the
  // section left it with one question in the accordion and two loose in the
  // article, where the auto-numbering then badged them as sections 02 and 03.
  // No post in the set has a genuine section after its FAQ.
  const isQuestionHeading = (el) => el.textContent.trim().endsWith("?");
  let end = blocks.length;
  for (let i = faqStart + 1; i < blocks.length; i += 1) {
    const level = headingLevel(blocks[i]);
    if (level && level <= faqLevel && !isQuestionHeading(blocks[i])) {
      end = i;
      break;
    }
  }

  const faqs = [];
  let current = null;
  const flush = () => {
    if (current && current.answerHtml.trim()) faqs.push(current);
  };

  for (const el of blocks.slice(faqStart + 1, end)) {
    if (isQuestion(el, faqLevel)) {
      flush();
      current = {
        question: el.textContent.replace(/\s+/g, " ").trim(),
        answerHtml: "",
        answerText: "",
      };
    } else if (current) {
      current.answerHtml += el.outerHTML;
      current.answerText += `${el.textContent.replace(/\s+/g, " ").trim()} `;
    }
  }
  flush();

  // A heading called "FAQs" with nothing parseable under it is left alone:
  // better to show it as ordinary prose than to silently drop the content.
  const articleBlocks = faqs.length
    ? blocks.filter((_, i) => i < faqStart || i >= end)
    : blocks;

  // Indexed after the FAQ is removed, so its questions never appear in the
  // contents list -- they have their own section further down the page.
  enhanceTables(doc);
  markSubheadings(articleBlocks, doc);
  markCallouts(articleBlocks);
  const headings = indexHeadings(articleBlocks, doc);

  return {
    articleHtml: articleBlocks.map((el) => el.outerHTML).join(""),
    faqs: faqs.map((faq) => ({ ...faq, answerText: faq.answerText.trim() })),
    headings,
    summaryHtml,
  };
}

function toPlainText(html) {
  const blocks = parseBlocks(html);
  if (!blocks) return "";
  return blocks
    .map((el) => el.textContent)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * True when the WordPress excerpt is just the opening of the post.
 *
 * WordPress auto-generates the excerpt from the first ~55 words unless an
 * author writes one, so nine of the thirteen posts have an excerpt that is
 * literally the first paragraph. Printing it above the article and then
 * again as the article's opening line reads like a mistake.
 */
// Compared on letters and digits alone. WordPress builds the excerpt by
// stripping tags from the content, and where that leaves whitespace differs
// from how the blocks are joined when reading them back -- "Overheating Heat"
// against "OverheatingHeat" is the same text but not the same string.
//
// Getting this wrong is not a near miss. A missed match means the excerpt is
// taken for author-written, so heading text can end up in the summary panel
// while the intro paragraph stays duplicated in the body below it -- exactly
// the thing the panel was reworked to avoid.
function normaliseForCompare(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function excerptRepeatsBody(excerptHtml, contentHtml) {
  const excerpt = normaliseForCompare(
    toPlainText(excerptHtml).replace(/\[[…\.]+\]\s*$/, "")
  );
  if (excerpt.length < 40) return false;
  return normaliseForCompare(toPlainText(contentHtml)).startsWith(
    excerpt.slice(0, 80)
  );
}
